import z from "zod";
import createEnv from "@/common/env/createEnv";

const { env, getEnv, getEnvOrFail } = createEnv({
  server: {
    // Server environment variables
    COHESION_SOURCE_KEY: z.string().optional(),
    COHESION_WRITE_KEY: z.string().optional(),
    SMART_SEARCH_API_KEY: z.string().optional(),
    SMART_SEARCH_ENDPOINT: z.string().optional(),
    LOG_LEVEL: z
      .enum(["debug", "info", "warn", "error", "trace", "fatal"])
      .optional()
      .default("info"),
    DISABLE_SAFELIST: z.enum(["true", "false"]).optional().default("false"),
    FAUST_SECRET_KEY: z.string().optional(),
    CONTENTFUL_SPACE_ID: z.string().optional(),
    CONTENTFUL_DELIVERY_API_KEY: z.string().optional(),
    CONTENTFUL_PREVIEW_API_KEY: z.string().optional(),
    SEGMENT_WRITE_KEY: z.string().optional(),
    PRETTY_PRINT_LOGS: z.enum(["true", "false"]).optional().default("false"),
    DISABLE_ROBOTS: z.enum(["true", "false"]).optional(),
    EXTERNAL_PARTNER_API_BASE_URL: z.string().url().optional(),
    COOKIE_DOMAIN: z.string().optional(),
    OAUTH_ID: z.string().optional(),
    OAUTH_SECRET: z.string().optional(),
    SECURE_PREVIEW_USERNAME: z.string().optional(),
    SECURE_PREVIEW_PASSWORD: z.string().optional(),
    ENABLE_BOT_BLOCKING: z.enum(["true", "false"]).optional(),
    ONE_TRUST_SCRIPT_ID: z.string().optional(),
    GTM_CONTAINER_ID: z.string().optional(),

    // Marketplace - PRODUCT API
    PRODUCT_API_BASE_URL: z.string().url().optional(),
    PRODUCT_API_KEY: z.string().optional(),
    PRODUCT_API_MULTI_DEV_ID: z.string().optional(),

    // Mobius - RV Inventory
    MOBIUS_API_BASE_URL: z.string().url().optional(),
    MOBIUS_API_KEY: z.string().optional(),
  },
  client: {
    // Client environment variables
    NEXT_PUBLIC_SITE_URL: z.string().url(),
    NEXT_PUBLIC_COHESION_CONSENT_REQUIRED: z.enum(["true", "false"]).optional(),
    NEXT_PUBLIC_WORDPRESS_URL: z.string().url(),
    NEXT_PUBLIC_APP_VERSION: z.string(),
    NEXT_PUBLIC_DISABLE_PERSONALIZED_EXPERIENCE: z
      .enum(["true", "false"])
      .optional(),
    NEXT_PUBLIC_COMMERCE_COORDINATOR_BASE_URL: z.string().url().optional(),
    NEXT_PUBLIC_COMMERCE_COORDINATOR_ENABLE_PROGRAMS: z
      .enum(["true", "false"])
      .optional(),
    // Feature flags
    NEXT_PUBLIC_FEATURE_DEGREE_DETAIL_REDESIGN_TRAFFIC: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_FEATURE_EXEC_ED_ATF_REDESIGN_TRAFFIC: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_FEATURE_PRODUCT_SUMMARY_SOURCE: z
      .enum(["algolia", "productApi", "shadow"])
      .optional(),
    NEXT_PUBLIC_FEATURE_PERSONALIZED_RESULTS: z
      .enum(["true", "false"])
      .optional(),
    NEXT_PUBLIC_FEATURE_INSTRUCTOR_SOURCE: z
      .enum(["algolia", "productApi", "shadow"])
      .optional(),
    NEXT_PUBLIC_FEATURE_COURSE_SOURCE: z
      .enum(["algolia", "productApi", "shadow"])
      .optional(),
    NEXT_PUBLIC_FEATURE_EDITORIAL_LISTINGS: z
      .enum(["affiliateWidget", "edxEl", "hide"])
      .optional(),
    NEXT_PUBLIC_FEATURE_PARTNER_SOURCE: z
      .enum(["algolia", "productApi", "shadow"])
      .optional(),
    NEXT_PUBLIC_FEATURE_PROGRAM_SOURCE: z
      .enum(["algolia", "productApi", "shadow"])
      .optional(),
    NEXT_PUBLIC_FEATURE_OC_PROMO_BANNER: z.enum(["legacy", "test"]).optional(),
    NEXT_PUBLIC_FEATURE_HOMEPAGE_PROMO_BANNER: z
      .enum(["legacy", "test"])
      .optional(),
    NEXT_PUBLIC_MOBILE_BANNER_KEY: z.string().optional(),
    // Authorizer, ie track selection
    NEXT_PUBLIC_AUTH_DOMAIN: z.string().optional(),
  },
  runtimeEnv: {
    NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
    NEXT_PUBLIC_COHESION_CONSENT_REQUIRED:
      process.env.NEXT_PUBLIC_COHESION_CONSENT_REQUIRED,
    NEXT_PUBLIC_WORDPRESS_URL: process.env.NEXT_PUBLIC_WORDPRESS_URL,
    NEXT_PUBLIC_APP_VERSION: process.env.NEXT_PUBLIC_APP_VERSION,
    NEXT_PUBLIC_DISABLE_PERSONALIZED_EXPERIENCE:
      process.env.NEXT_PUBLIC_DISABLE_PERSONALIZED_EXPERIENCE,
    NEXT_PUBLIC_COMMERCE_COORDINATOR_BASE_URL:
      process.env.NEXT_PUBLIC_COMMERCE_COORDINATOR_BASE_URL,
    NEXT_PUBLIC_COMMERCE_COORDINATOR_ENABLE_PROGRAMS:
      process.env.NEXT_PUBLIC_COMMERCE_COORDINATOR_ENABLE_PROGRAMS,
    NEXT_PUBLIC_FEATURE_DEGREE_DETAIL_REDESIGN_TRAFFIC:
      process.env.NEXT_PUBLIC_FEATURE_DEGREE_DETAIL_REDESIGN_TRAFFIC,
    NEXT_PUBLIC_FEATURE_EXEC_ED_ATF_REDESIGN_TRAFFIC:
      process.env.NEXT_PUBLIC_FEATURE_EXEC_ED_ATF_REDESIGN_TRAFFIC,
    NEXT_PUBLIC_FEATURE_PRODUCT_SUMMARY_SOURCE:
      process.env.NEXT_PUBLIC_FEATURE_PRODUCT_SUMMARY_SOURCE,
    NEXT_PUBLIC_FEATURE_PERSONALIZED_RESULTS:
      process.env.NEXT_PUBLIC_FEATURE_PERSONALIZED_RESULTS,
    NEXT_PUBLIC_FEATURE_INSTRUCTOR_SOURCE:
      process.env.NEXT_PUBLIC_FEATURE_INSTRUCTOR_SOURCE,
    NEXT_PUBLIC_FEATURE_COURSE_SOURCE:
      process.env.NEXT_PUBLIC_FEATURE_COURSE_SOURCE,
    NEXT_PUBLIC_FEATURE_EDITORIAL_LISTINGS:
      process.env.NEXT_PUBLIC_FEATURE_EDITORIAL_LISTINGS,
    NEXT_PUBLIC_FEATURE_OC_PROMO_BANNER:
      process.env.NEXT_PUBLIC_FEATURE_OC_PROMO_BANNER,
    NEXT_PUBLIC_MOBILE_BANNER_KEY: process.env.NEXT_PUBLIC_MOBILE_BANNER_KEY,
    NEXT_PUBLIC_AUTH_DOMAIN: process.env.NEXT_PUBLIC_AUTH_DOMAIN,
  },
});

export { env, getEnv, getEnvOrFail };
